import { useEffect, useMemo, useRef } from '@wordpress/element';
import { useEvent, useIsomorphicLayoutEffect } from '@wordpress/compose';
import { ThemeContext } from './context.ts';
import { useThemeProviderStyles } from './use-theme-provider-styles.ts';
import { type ThemeProviderProps } from './types.ts';
import styles from './style.module.css';

// Dev-only: count active root providers per document so we can warn when more
// than one is mounted on the same document (their forwarded `html` styles
// would conflict). Keyed weakly so iframe documents don't leak.
const rootProviderCountByDocument = new WeakMap< Document, number >();

/**
 * Context provider that generates a theme from a set of seed color values and
 * configuration, producing a set of design token overrides as CSS custom
 * properties.
 */
export const ThemeProvider = ( {
	children,
	color = {},
	cursor,
	cornerRadius,
	isRoot = false,
	onColorWarnings,
}: ThemeProviderProps ) => {
	const { themeProviderStyles, resolvedSettings, colorWarnings } =
		useThemeProviderStyles( {
			color,
			cursor,
			cornerRadius,
		} );

	const cornerRadiusPreset = resolvedSettings.cornerRadius ?? 'subtle';
	const onColorWarningsEvent = useEvent( onColorWarnings );

	const contextValue = useMemo(
		() => ( {
			resolvedSettings,
		} ),
		[ resolvedSettings ]
	);

	const wrapperRef = useRef< HTMLDivElement >( null );

	useEffect( () => {
		if ( colorWarnings !== undefined ) {
			onColorWarningsEvent( colorWarnings );
		}
	}, [ colorWarnings, onColorWarningsEvent ] );

	// For root providers, mirror the wrapper's custom properties and preset
	// attributes onto the document element of the wrapper's own document
	// (which may be an iframe) so they reach portals and content rendered
	// outside the React subtree. `html` is shared, so restore prior values on
	// cleanup rather than replacing its complete style or attribute state.
	useIsomorphicLayoutEffect( () => {
		if ( ! isRoot ) {
			return;
		}
		const doc = wrapperRef.current?.ownerDocument;
		if ( ! doc ) {
			return;
		}
		const root = doc.documentElement;

		if ( process.env.NODE_ENV !== 'production' ) {
			const active = rootProviderCountByDocument.get( doc ) ?? 0;
			if ( active > 0 ) {
				// eslint-disable-next-line no-console
				console.warn(
					'ThemeProvider: More than one root provider (`isRoot`) is mounted on the same document. Their forwarded document-level styles conflict, and unmounting one can reset the others. Render a single root provider per document.'
				);
			}
			rootProviderCountByDocument.set( doc, active + 1 );
		}

		const previous = new Map< string, string >();
		const applied: string[] = [];
		const previousRootProvider = root.getAttribute(
			'data-wpds-root-provider'
		);
		const previousCornerRadius = root.getAttribute(
			'data-wpds-corner-radius'
		);

		root.setAttribute( 'data-wpds-root-provider', 'true' );
		root.setAttribute( 'data-wpds-corner-radius', cornerRadiusPreset );

		for ( const [ rawKey, rawValue ] of Object.entries(
			themeProviderStyles
		) ) {
			if (
				! rawKey.startsWith( '--' ) ||
				rawValue === null ||
				rawValue === undefined
			) {
				continue;
			}
			previous.set( rawKey, root.style.getPropertyValue( rawKey ) );
			root.style.setProperty( rawKey, String( rawValue ) );
			applied.push( rawKey );
		}

		return () => {
			if ( process.env.NODE_ENV !== 'production' ) {
				const active = rootProviderCountByDocument.get( doc ) ?? 1;
				if ( active <= 1 ) {
					rootProviderCountByDocument.delete( doc );
				} else {
					rootProviderCountByDocument.set( doc, active - 1 );
				}
			}

			for ( const key of applied ) {
				const prev = previous.get( key );
				if ( prev ) {
					root.style.setProperty( key, prev );
				} else {
					root.style.removeProperty( key );
				}
			}

			if ( previousRootProvider === null ) {
				root.removeAttribute( 'data-wpds-root-provider' );
			} else {
				root.setAttribute(
					'data-wpds-root-provider',
					previousRootProvider
				);
			}

			if ( previousCornerRadius === null ) {
				root.removeAttribute( 'data-wpds-corner-radius' );
			} else {
				root.setAttribute(
					'data-wpds-corner-radius',
					previousCornerRadius
				);
			}
		};
	}, [ cornerRadiusPreset, isRoot, themeProviderStyles ] );

	return (
		<div
			ref={ wrapperRef }
			data-wpds-root-provider={ isRoot || undefined }
			data-wpds-corner-radius={ cornerRadiusPreset }
			className={ styles.wrapper }
			style={ themeProviderStyles }
		>
			<ThemeContext.Provider value={ contextValue }>
				{ children }
			</ThemeContext.Provider>
		</div>
	);
};
